import React from "react";
import { useTranslation } from "react-i18next";
import JsonApiService from "../services/jsonapiService";
import { visionURL } from "../config/environment";
import { deserialize } from "../redux/utils/jsona";
import { useSnackbarNotifications } from "./snackbar";

const employeeProfileService = new JsonApiService("users", {
  baseUrl: `${visionURL}/admin_console/api/administration/employees`,
  credentials: "include"
});

export function useEmployeeProfile(employeeId) {
  const { t } = useTranslation();
  const {
    displaySuccessMessage,
    displayErrorMessage
  } = useSnackbarNotifications();
  const [employeeProfile, setEmployeeProfile] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // load employee profile
  const getEmployeeProfile = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await employeeProfileService.find(employeeId);
      const data = await response.json();
      const normalizedData = deserialize(data);
      setEmployeeProfile(normalizedData);
      setIsLoading(false);
    } catch (error) {
      setError(error.message);
      setEmployeeProfile(null);
      setIsLoading(false);
    }
  }, []);

  const updateEmployeeProfile = React.useCallback(
    async ({ profileImage, ...profileData }) => {
      try {
        setIsLoading(true);
        setError("");
        // update employee info
        await employeeProfileService.update(
          employeeId,
          JSON.stringify({
            data: {
              type: "userDetails",
              id: employeeId,
              attributes: {
                ...profileData,
                ...(profileImage && {
                  profileImage: await toDataURL(profileImage)
                })
              }
            }
          })
        );
        // fetch new user info
        await getEmployeeProfile();
        displaySuccessMessage(t("profile.successMsg"));
      } catch (error) {
        displayErrorMessage(t("profile.errorMsg"));
        // manage state
        setError(error.message);
        setEmployeeProfile(null);
        setIsLoading(false);
      }
    }
  );

  // initialize with data
  React.useEffect(() => {
    getEmployeeProfile();
  }, []);

  return {
    isLoading,
    error,
    employeeProfile,
    updateEmployeeProfile,
    getEmployeeProfile
  };
}

async function toDataURL(file) {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
}
